import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { IAccountsByCustomer } from 'src/app/interfaces/self-serve/iaccounts-by-customer';
import { ITeam } from 'src/app/interfaces/reportingV2/iteams';
import { IPracticeAssignmentStatsRequest } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-request';
import { IPracticeAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-response';
import { ICertificationAssignmentStatsRequest } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-request';
import { ICertificationAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-response';
import { IIndividualUser } from 'src/app/interfaces/reportingV2/iindividual-user';
import { IUserEngagementStatsRequest } from 'src/app/interfaces/reportingV2/iuser-engagement-stats-request';
import { IUserEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iuser-engagement-stats-response';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { IPracticeSimulation } from 'src/app/interfaces/reportingV2/ipractice-simulation';
import { ICertificationSimulation } from 'src/app/interfaces/reportingV2/icertification-simulation';
import { IIndividualAssignmentEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iindividual-assignment-engagement-stats-response';
import { ICertificationAssignmentUserStatsRequest } from 'src/app/interfaces/reportingV2/icertification-assignment-user-stats-request';
import { ICertificationAssignmentUserStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-user-stats-response';
import { IUserAssignmentRequest } from 'src/app/interfaces/iuser-assignment-request';
import { IUserAssignmentResponse } from 'src/app/interfaces/iuser-assignment-response';
import { IUserSummaryRequest } from 'src/app/interfaces/reportingV2/iuser-summary-request';
import { IUserSummaryResponse } from 'src/app/interfaces/reportingV2/iuser-summary-response';
import { IUserAssignmentMetricsResponse } from 'src/app/interfaces/iuser-assignment-metrics-response';
import { IUserSessionRequest } from 'src/app/interfaces/reportingV2/iuser-session-stats-request';
import { IUserSessionResponse } from 'src/app/interfaces/reportingV2/iuser-session-stats-response';
import { IPersonalizedPracticeModulesRequest } from 'src/app/interfaces/reportingV2/ipersonalized-practice-modules-request';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';
import { IPersonalizedPracticeCallStatsRequest } from 'src/app/interfaces/reportingV2/ipersonalized-practice-call-stats-request';
import { IPersonalizedPracticeSessionReport } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session-report';
import { ISimulationViewStatsResponse } from 'src/app/interfaces/reportingV2/isimulation-view-stats-response';
import { IMessageAdherenceAverageScoresRequest } from 'src/app/interfaces/reportingV2/imessage-adherence-average-scores-request';
import { IMessageAdherenceAverageScoresResponse } from 'src/app/interfaces/reportingV2/imessage-adherence-average-scores-response';
import { ISkillScoreStatsRequest } from 'src/app/interfaces/reportingV2/iskill-score-stats-request';
import { ISkillScoreStatsResponse } from 'src/app/interfaces/reportingV2/iskill-score-stats-response';

import { IAverageObjectiveScoresRequest } from 'src/app/interfaces/reportingV2/iaverage-objective-scores-request';
import { IAverageMessageAdherenceV2ScoresRequest } from 'src/app/interfaces/reportingV2/iaverage-message-adherence-v2-scores-request';
import { IMessageAdherenceV2ThemeScore } from 'src/app/interfaces/reportingV2/iaverage-message-adherence-v2-scores-response';
import { IObjectivesStats } from 'src/app/interfaces/iobjective-stats';
import { IHighlightCard, IHighlightCardRequest } from 'src/app/interfaces/reportingV2/ihighlight-card';
import { IModuleAssignmentStatsRequest } from 'src/app/interfaces/reportingV2/imodule-assignment-stats-request';
import { IModuleAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/imodule-assignment-stats-response';
import { IModuleAssignmentHighlightCardRequest, IModuleAssignmentHighlightCard } from 'src/app/interfaces/reportingV2/imodule-assignment-highlight-card';
@Injectable({
  providedIn: 'root',
})
export class ReportingDashboardService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getAccounts(options?: { context?: HttpContext }): Observable<IAccountsByCustomer[]> {
    return this.http
      .get<any>(
        `${this.backendUrl}/account/getCustomerWithAccountsForCurrentUser/`,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCustomerWithAccountsForCurrentUser: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getCustomerWithAccountsForCurrentUser`
          )
        )
      );
  }

  getTeams(accountUuid: string, options?: { context?: HttpContext }): Observable<ITeam[]> {
    return this.http
      .get<any>(
        `${this.backendUrl}/team/getUserTeamsWithManagerRole?accountUuid=${accountUuid}`,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUserTeamsWithManagerRole: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getUserTeamsWithManagerRole`
          )
        )
      );
  }

  getPracticeAssignmentStats(
    payload: IPracticeAssignmentStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<IPracticeAssignmentStatsResponse[]> {
    return this.http
      .post<IPracticeAssignmentStatsResponse[]>(
        `${this.backendUrl}/analytics/assignmentRollupView/practiceAssignmentStats`,
        payload,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPracticeAssignmentStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<
            IPracticeAssignmentStatsResponse[]
          >(`/analytics/assignmentRollupView/practiceAssignmentStats`)
        )
      );
  }

  getCertificationAssignmentStats(
    request: ICertificationAssignmentStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<ICertificationAssignmentStatsResponse[]> {
    return this.http
      .post<ICertificationAssignmentStatsResponse[]>(
        `${this.backendUrl}/analytics/assignmentRollupView/certificationAssignmentStats`,
        request,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCertificationAssignmentStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<
            ICertificationAssignmentStatsResponse[]
          >(`getCertificationAssignmentStats`)
        )
      );
  }

  getUsersData(
    accountUuid: string,
    teamUuids?: string[],
    options?: { context?: HttpContext }
  ): Observable<IIndividualUser[]> {
    const body = {
      accountUuid,
      teamUuids: teamUuids?.length ? teamUuids : null,
    };

    return this.http
      .post<IIndividualUser[]>(
        `${this.backendUrl}/analytics/usersView/getUsersData`,
        body,
        {
        context: options?.context
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getUsersData: ${outcome} users`);
        }),
        catchError(
          this.errorHandlingService.handleError<IIndividualUser[]>(
            `getUsersData`
          )
        )
      );
  }

  getUserEngagementStats(
    request: IUserEngagementStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<IUserEngagementStatsResponse[]> {
    return this.http
      .post<IUserEngagementStatsResponse[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/userEngagementStats`,
        request,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUserEngagementStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IUserEngagementStatsResponse[]>(
            `getUserEngagementStats`
          )
        )
      );
  }

  getPracticeSimulationMetrics(
    accountUuid: string,
    moduleAssignmentUuid: string,
    teamUuids: string[],
    options?: { context?: HttpContext }
  ): Observable<IPracticeSimulation[]> {
    const body = {
      accountUuid,
      moduleAssignmentUuid,
      teamUuids,
    };

    return this.http
      .post<IPracticeSimulation[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/simulationMetrics`,
        body,
        {
          context: options?.context
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getSimulationMetrics: ${outcome} simulation metrics`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPracticeSimulation[]>(
            `getSimulationMetrics`
          )
        )
      );
  }

  getCertificationSimulationMetrics(
    accountUuid: string,
    moduleAssignmentUuid: string,
    teamUuids: string[],
    options?: { context?: HttpContext }
  ): Observable<ICertificationSimulation[]> {
    const body = {
      accountUuid,
      moduleAssignmentUuid,
      teamUuids,
    };

    return this.http
      .post<ICertificationSimulation[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/certificationAssignmentSimulationStats`,
        body,
        {
          context: options?.context
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(
            `getCertificationSimulationStats: ${outcome} certification simulation stats`
          );
        }),
        catchError(
          this.errorHandlingService.handleError<ICertificationSimulation[]>(
            `getCertificationSimulationStats`
          )
        )
      );
  }

  getSimulationStats(
    accountUuid: string,
    moduleAssignmentUuid: string,
    simulationUuid: string,
    teamUuids: string[] = [],
    options?: { context?: HttpContext }
  ): Observable< ISimulationViewStatsResponse > {
    const body = { accountUuid, moduleAssignmentUuid, simulationUuid, teamUuids };

    return this.http
      .post< ISimulationViewStatsResponse >(
        `${this.backendUrl}/analytics/individualSimulationView/simulationStats`,
        body,
        { context: options?.context }
      )
      .pipe(
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getSimulationStats: ${outcome} simulation stats`);
        }),
        catchError(this.errorHandlingService.handleError<ISimulationViewStatsResponse>(
            `getSimulationStats failed`
          ))
      );
  }

  getAverageObjectiveScores(
    request: IAverageObjectiveScoresRequest,
    options?: { context?: HttpContext }
  ): Observable<IObjectivesStats[]> {
    return this.http
      .post<IObjectivesStats[]>(
        `${this.backendUrl}/analytics/individualSimulationView/getAverageObjectiveScores`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result ?? []),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getAverageObjectiveScores: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IObjectivesStats[]>(
            `getAverageObjectiveScores failed`
          )
        )
      );
  }

  getAverageMessageAdherenceV2Scores(
    request: IAverageMessageAdherenceV2ScoresRequest,
    options?: { context?: HttpContext }
  ): Observable<IMessageAdherenceV2ThemeScore[]> {
    return this.http
      .post<IMessageAdherenceV2ThemeScore[]>(
        `${this.backendUrl}/analytics/individualSimulationView/getAverageMessageAdherenceV2Scores`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result ?? []),
        tap((res) => {
          const outcome = res?.length ? 'fetched' : 'did not find';
          console.log(`getAverageMessageAdherenceV2Scores: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IMessageAdherenceV2ThemeScore[]>(
            `getAverageMessageAdherenceV2Scores failed`
          )
        )
      );
  }

  getIndividualAssignmentEngagementStatus(
    request: IAssignmentViewStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<IIndividualAssignmentEngagementStatsResponse> {
    return this.http
      .post<IIndividualAssignmentEngagementStatsResponse>(
        `${this.backendUrl}/analytics/individualAssignmentView/engagementStatus`,
        request,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getIndividualAssignmentEngagementStatus: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IIndividualAssignmentEngagementStatsResponse>(
            `getIndividualAssignmentEngagementStatus`
          )
        )
      );
  }

  getAssignmentViewStats(
    payload: IAssignmentViewStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<IAssignmentViewStatsResponse[]> {
    return this.http
      .post<IAssignmentViewStatsResponse[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/sessionStats`,
        payload,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getAssignmentViewStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IAssignmentViewStatsResponse[]>(
            `getAssignmentViewStats`
          )
        )
      );
  }

  getCertificationAssignmentUserStats(
    request: ICertificationAssignmentUserStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<ICertificationAssignmentUserStatsResponse[]> {
    return this.http
      .post<ICertificationAssignmentUserStatsResponse[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/certificationAssignmentUserStats`,
        request,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(
            `getCertificationAssignmentUserStats: ${outcome} certification user stats`
          );
        }),
        catchError(
          this.errorHandlingService.handleError<
            ICertificationAssignmentUserStatsResponse[]
          >(`getCertificationAssignmentUserStats`)
        )
      );
  }

  getUserSummary(
    request: IUserSummaryRequest,
    options?: { context?: HttpContext }
  ): Observable<IUserSummaryResponse> {
    return this.http
      .post<IUserSummaryResponse>(
        `${this.backendUrl}/analytics/usersView/getUserSummary`,
        request,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUserSummary: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IUserSummaryResponse>(
            `getUserSummary`
          )
        )
      );
  }

  getUserAssignments(
    payload: IUserAssignmentRequest,
    options?: { context?: HttpContext }
  ): Observable<IUserAssignmentResponse[]> {
    return this.http
      .post<IUserAssignmentResponse[]>(
        `${this.backendUrl}/analytics/usersView/getUserAssignments`,
        payload,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUserAssignments: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IUserAssignmentResponse[]>(
            `getUserAssignments`
          )
        )
      );
  }

  getUserAssignmentMetrics(
    payload: IUserAssignmentRequest,
    options?: { context?: HttpContext }
  ): Observable<IUserAssignmentMetricsResponse> {
    return this.http
      .post<IUserAssignmentMetricsResponse>(
        `${this.backendUrl}/analytics/usersView/getUserAssignmentMetrics`,
        payload,
        {
        context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUserAssignmentMetrics: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<
            IUserAssignmentMetricsResponse
          >(`getUserAssignmentMetrics`)
        )
      );
  }

  getUserSessions(
    request: IUserSessionRequest,
    options?: { context?: HttpContext }
  ): Observable<IUserSessionResponse[]> {
    return this.http
      .post<IUserSessionResponse[]>(`${this.backendUrl}/analytics/usersView/getIndividualUserSessions`, 
        request, 
        {
        context: options?.context
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getUserSessions: ${outcome} sessions`);
        }),
        catchError(
          this.errorHandlingService.handleError<IUserSessionResponse[]>('getUserSessions')
        )
      );
  }

  getPersonalizedPracticeModules(
    request: IPersonalizedPracticeModulesRequest,
    options?: { context?: HttpContext }
  ): Observable<IPersonalPracticeSim[]> {
    return this.http
      .post<IPersonalPracticeSim[]>(
        `${this.backendUrl}/report/personalizedPractice/getModulesForDashboard`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getPersonalizedPracticeModules: ${outcome} modules`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPersonalPracticeSim[]>(
            'getPersonalizedPracticeModules'
          )
        )
      );
  }

  getPersonalizedPracticeCallStats(
    request: IPersonalizedPracticeCallStatsRequest
  ): Observable<IPersonalizedPracticeSessionReport[]> {
    return this.http
      .post<IPersonalizedPracticeSessionReport[]>(
        `${this.backendUrl}/report/personalizedPractice/getPersonalizedPracticeCallStats`,
        request
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getPersonalizedPracticeCallStats: ${outcome} sessions`);
        }),
        catchError(
          this.errorHandlingService.handleError<
            IPersonalizedPracticeSessionReport[]
          >('getPersonalizedPracticeCallStats')
        )
      );
  }

  getMessageAdherenceAverageScores(
    request: IMessageAdherenceAverageScoresRequest,
    options?: { context?: HttpContext }
  ): Observable<IMessageAdherenceAverageScoresResponse[]> {
    return this.http
      .post<IMessageAdherenceAverageScoresResponse[]>(
        `${this.backendUrl}/analytics/individualSimulationView/messageAdherenceAverageScores`,
        request,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getMessageAdherenceAverageScores: ${outcome} message adherence scores`);
        }),
        catchError(
          this.errorHandlingService.handleError<
            IMessageAdherenceAverageScoresResponse[]
          >('getMessageAdherenceAverageScores')
        )
      );
  }

  getSkillScoreStats(
    request: ISkillScoreStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<ISkillScoreStatsResponse[]> {
    return this.http
      .post<ISkillScoreStatsResponse[]>(
        `${this.backendUrl}/analytics/individualSimulationView/skillScoreStats`,
        request,
        {
          context: options?.context
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getSkillScoreStats: ${outcome} skill score stats`);
        }),
        catchError(
          this.errorHandlingService.handleError<ISkillScoreStatsResponse[]>(
            'getSkillScoreStats'
          )
        )
      );
  }

  getHighlightCards(request: IHighlightCardRequest, options?: { context?: HttpContext }): Observable<IHighlightCard[]> {
    return this.http
      .post<IHighlightCard[]>(
        `${this.backendUrl}/analytics/usersView/getUserSessionsSummary`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getHighlightCards: ${outcome} highlight cards`);
        }),
        catchError(
          this.errorHandlingService.handleError<IHighlightCard[]>(
            'getHighlightCards'
          )
        )
      );
  }

  getModuleAssignmentStats(
    request: IModuleAssignmentStatsRequest,
    options?: { context?: HttpContext }
  ): Observable<IModuleAssignmentStatsResponse> {
    return this.http
      .post<IModuleAssignmentStatsResponse>(
        `${this.backendUrl}/analytics/individualAssignmentView/moduleAssignmentStats`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModuleAssignmentStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IModuleAssignmentStatsResponse>(
            'getModuleAssignmentStats'
          )
        )
      );
  }

  getModuleAssignmentHighlightCards(
    request: IModuleAssignmentHighlightCardRequest,
    options?: { context?: HttpContext }
  ): Observable<IModuleAssignmentHighlightCard[]> {
    return this.http
      .post<IModuleAssignmentHighlightCard[]>(
        `${this.backendUrl}/analytics/individualAssignmentView/getModuleAssignmentStatsSummary`,
        request,
        { context: options?.context }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res && res.length ? 'fetched' : 'did not find';
          console.log(`getModuleAssignmentHighlightCards: ${outcome} highlight cards`);
        }),
        catchError(
          this.errorHandlingService.handleError<IModuleAssignmentHighlightCard[]>(
            'getModuleAssignmentHighlightCards'
          )
        )
      );
  }
  }
